import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  FileText,
  User,
  TrendingUp,
  Award,
  UserPlus,
  Share2,
  BarChart3,
  Calendar,
  MessageSquare,
  AlertTriangle,
  Briefcase,
  Bell,
  LogOut,
  Lock,
  Trophy,
  X
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTier } from '../context/TierContext';
import { useChatWebSocketContext } from '../context/ChatWebSocketContext';
import { chatApi, companyRegistrationApi } from '../services/api';
import { getTierColor } from '../utils/tierColors';
import { getLogoOrInitials } from '../utils/companyLogo';
import UpgradePrompt from './UpgradePrompt';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';

const Sidebar = ({ isOpen, onClose, onRestoreFocus }) => {
  const location = useLocation();
  const { member, user, logout } = useAuth();
  const { hasFeatureAccess, isFeatureEnabled, hasRestriction, getRestrictionMessage, pointsSystemEnabled, loading: tierLoading } = useTier();
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [blockedFeature, setBlockedFeature] = useState(null);
  const [messageUnreadCount, setMessageUnreadCount] = useState(0);
  const [companyRegistration, setCompanyRegistration] = useState(null);
  const [showUpgradeBlockedDialog, setShowUpgradeBlockedDialog] = useState(false);
  const [brandLogoFailed, setBrandLogoFailed] = useState(false);
  const closeButtonRef = useRef(null);
  const sidebarRef = useRef(null);
  const wsContext = useChatWebSocketContext();
  const featureToRestriction = { quotes: 'quotes', events: 'events', messaging: 'chat', ma_portal: 'ma', directory: 'directory' };
  const currentMemberId = member?.member_id;
  const navigate = useNavigate();
  
  // Fallback values for display
  const displayName = member?.individual_name || user?.full_name || 'Member';
  const displayCompany = member?.company_name || 'Company';
  const displayTier = member?.tier || 'Silver';
  const displayPoints = member?.reward_points || 0;
  const isRegisteredUser = (member?.tier || '').toLowerCase() === 'registered user';
  const isInvitedTeamMember =
    member?.has_admin_rights === false ||
    member?.role === 'Company Member' ||
    Boolean(member?.invited_by);
  const isCompanyApproved = companyRegistration?.status?.toLowerCase() === 'approved';
  const shouldBlockUpgrade = isRegisteredUser && !isInvitedTeamMember && !isCompanyApproved;
  
  const tierColor = getTierColor(displayTier);

  const fetchUnreadMessageCount = useCallback(async () => {
    if (!currentMemberId) {
      setMessageUnreadCount(0);
      return;
    }
    try {
      const response = await chatApi.getConversations(currentMemberId);
      const conversations = response.data || [];
      const totalUnread = conversations.reduce(
        (sum, conversation) => sum + (conversation.unread_count?.[currentMemberId] ?? 0),
        0
      );
      setMessageUnreadCount(totalUnread);
    } catch (error) {
      console.error('Failed to fetch unread message count:', error);
    }
  }, [currentMemberId]);

  useEffect(() => {
    fetchUnreadMessageCount();
  }, [fetchUnreadMessageCount]);

  useEffect(() => {
    const registerHandlers = wsContext?.registerHandlers;
    if (!registerHandlers || !currentMemberId) return;

    const unregister = registerHandlers(
      (payload) => {
        if (payload?.type !== 'new_message') return;
        const senderId = payload?.message?.sender_id;
        if (senderId === currentMemberId) return;
        setMessageUnreadCount((prev) => prev + 1);
      },
      (payload) => {
        if (payload?.type !== 'messages_read') return;
        fetchUnreadMessageCount();
      }
    );

    return unregister;
  }, [wsContext, currentMemberId, fetchUnreadMessageCount]);

  useEffect(() => {
    if (location.pathname === '/chat') {
      fetchUnreadMessageCount();
    }
  }, [location.pathname, fetchUnreadMessageCount]);

  useEffect(() => {
    let cancelled = false;

    if (!isRegisteredUser) return undefined;

    companyRegistrationApi.getMyRegistration()
      .then((response) => {
        if (!cancelled && response.data?.exists) {
          setCompanyRegistration(response.data);
        }
      })
      .catch((error) => {
        if (!cancelled && error.response?.status !== 404) {
          console.error('Failed to fetch company registration:', error);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isRegisteredUser]);

  useEffect(() => {
    const handleUnreadCountChanged = () => {
      fetchUnreadMessageCount();
    };

    window.addEventListener('chat-unread-count-changed', handleUnreadCountChanged);
    return () => {
      window.removeEventListener('chat-unread-count-changed', handleUnreadCountChanged);
    };
  }, [fetchUnreadMessageCount]);

  useEffect(() => {
    if (!isOpen || showUpgradePrompt || showUpgradeBlockedDialog) return undefined;

    const focusFrame = window.requestAnimationFrame(() => closeButtonRef.current?.focus());

    const handleKeyDown = (event) => {
      const activeModal = document.activeElement?.closest?.(
        '[role="dialog"][aria-modal="true"], [role="alertdialog"][aria-modal="true"]'
      );
      if (activeModal && !sidebarRef.current?.contains(activeModal)) return;

      if (event.key === 'Escape') {
        onClose();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = sidebarRef.current?.querySelectorAll(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable?.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const activeElement = document.activeElement;

      if (event.shiftKey && (activeElement === first || !sidebarRef.current.contains(activeElement))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (activeElement === last || !sidebarRef.current.contains(activeElement))) {
        event.preventDefault();
        first.focus();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.cancelAnimationFrame(focusFrame);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose, showUpgradePrompt, showUpgradeBlockedDialog]);

  // Map paths to feature keys
  const pathToFeature = {
    '/dashboard': 'dashboard',
    '/directory': 'directory',
    '/quotes': 'quotes',
    '/event-connect': 'events',
    '/chat': 'messaging',
    '/profile': null, // Always accessible
    '/team': 'team',
    '/upgrade': null, // Always accessible
    '/rewards': 'rewards',
    '/colleagues': 'team',
    '/referrals': 'referrals',
    '/analytics': 'analytics',
    '/mergers-acquisitions': 'ma_portal',
    '/complaints': 'complaints',
    '/notifications': null,
    '/leaderboard': null
  };

  const menuItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', feature: 'dashboard' },
    { path: '/directory', icon: Users, label: 'Directory', feature: 'directory' },
    { path: '/quotes', icon: FileText, label: 'Quote Tool', feature: 'quotes' },
    { path: '/event-connect', icon: Calendar, label: 'Event Connect', feature: 'events' },
    { path: '/chat', icon: MessageSquare, label: 'Messages', feature: 'messaging' },
    { path: '/notifications', icon: Bell, label: 'Notifications', feature: null },
    { path: '/stories', icon: MessageSquare, label: 'Success Stories', feature: null },
    { path: '/leaderboard', icon: Trophy, label: 'Member Leaderboard', feature: null },
    { path: '/profile', icon: User, label: 'My Profile', feature: null },
    { path: '/team', icon: UserPlus, label: 'Team Management', feature: 'team' },
    { path: '/upgrade', icon: TrendingUp, label: 'Upgrade', feature: null },
    { path: '/rewards', icon: Award, label: 'Rewards', feature: 'rewards' },
    { path: '/colleagues', icon: Users, label: 'Company Branches', feature: 'team' },
    { path: '/referrals', icon: Share2, label: 'Refer Partner', feature: 'referrals' },
    { path: '/analytics', icon: BarChart3, label: 'Analytics', feature: 'analytics' },
    { path: '/mergers-acquisitions', icon: Briefcase, label: 'M&A', feature: 'ma_portal' },
    { path: '/complaints', icon: AlertTriangle, label: 'Complaints', feature: 'complaints' }
  ];
  const hideRewardsFeature = tierLoading || !pointsSystemEnabled;

  const handleMenuClick = (e, item) => {
    if (item.path === '/upgrade' && shouldBlockUpgrade) {
      e.preventDefault();
      onClose({ restoreFocus: false });
      setShowUpgradeBlockedDialog(true);
      return;
    }

    if (item.feature && !hasFeatureAccess(item.feature)) {
      e.preventDefault();
      onClose({ restoreFocus: false });
      setBlockedFeature(item.feature);
      setShowUpgradePrompt(true);
      return;
    }

    onClose();
  };

  const handleUpgradePromptClose = () => {
    setShowUpgradePrompt(false);
    onRestoreFocus();
  };

  const handleUpgradeBlockedDialogChange = (open) => {
    setShowUpgradeBlockedDialog(open);
    if (!open) {
      onRestoreFocus();
    }
  };

  return (
    <>
      {isOpen && (
        <button
          type="button"
          className="fixed inset-0 z-40 bg-slate-900/40 backdrop-blur-[1px] xl:hidden"
          aria-label="Close navigation menu"
          onClick={onClose}
        />
      )}
      <aside
        ref={sidebarRef}
        className={`fixed inset-y-0 left-0 z-50 flex h-[100dvh] w-72 max-w-[85vw] flex-col border-r border-gray-200 bg-white shadow-xl transition-transform duration-200 ease-out xl:w-64 xl:translate-x-0 xl:visible xl:pointer-events-auto xl:shadow-none ${
          isOpen ? 'translate-x-0 visible pointer-events-auto' : '-translate-x-full invisible pointer-events-none'
        }`}
        role={isOpen ? 'dialog' : undefined}
        aria-modal={isOpen || undefined}
        aria-label="Member navigation"
      >
        <div className="flex items-center gap-2 border-b border-gray-200 p-4 xl:p-6">
          <div className="min-w-0 flex-1">
            {!brandLogoFailed ? (
              <img
                src="/images/one-globe-company-logo.svg"
                alt="One Globe Alliance"
                className="h-14 w-full object-contain"
                onError={() => setBrandLogoFailed(true)}
              />
            ) : (
              <h1 className="text-xl font-bold text-blue-900">
                <span className="text-[#00B5D9]">O</span>
                <span className="text-[#C43BDF]">N</span>
                <span className="text-[#FF7A21]">E</span>
                {' '}Globe Alliance
              </h1>
            )}
          </div>
          <Button
            ref={closeButtonRef}
            type="button"
            variant="ghost"
            size="icon"
            className="min-h-[44px] min-w-[44px] shrink-0 xl:hidden"
            aria-label="Close navigation menu"
            onClick={onClose}
          >
            <X size={20} />
          </Button>
        </div>

      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center space-x-3">
          <img
            src={getLogoOrInitials(member, displayCompany)}
            alt={displayCompany}
            className="w-10 h-10 rounded-full object-cover"
            onError={(e) => { e.target.src = `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(displayName)}`; }}
          />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-900 truncate">{displayName}</p>
            <p className="text-xs text-gray-600 truncate">{displayCompany}</p>
          </div>
        </div>
        <div className={`mt-3 px-3 py-1 ${tierColor.bg} rounded-full text-center border ${tierColor.border}`}>
          <span className={`text-xs font-medium ${tierColor.text}`}>{displayTier} Member</span>
        </div>
      </div>

      <nav className="flex-1 p-4 overflow-y-auto">
        <TooltipProvider>
          <ul className="space-y-1">
            {menuItems.filter((item) => {
              if (item.feature === 'rewards' && hideRewardsFeature) return false;
              return !item.feature || isFeatureEnabled(item.feature);
            }).map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              const isLocked = item.feature && !hasFeatureAccess(item.feature);
              const restrictionType = featureToRestriction[item.feature];
              const hoverMessage = isLocked && restrictionType && hasRestriction(restrictionType)
                ? getRestrictionMessage(restrictionType)
                : 'Contact admin';

              return (
                <li key={item.path}>
                  {isLocked ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Link
                          to="#"
                          onClick={(e) => handleMenuClick(e, item)}
                          className="flex items-center justify-between px-4 py-3 rounded-lg transition-colors text-gray-400 hover:bg-gray-50 cursor-pointer"
                        >
                      <div className="flex items-center space-x-3">
                            <Icon size={20} />
                            <span className="text-sm font-medium">{item.label}</span>
                          </div>
                          {item.path === '/chat' && messageUnreadCount > 0 && (
                            <span className="bg-blue-600 text-white text-xs font-medium min-w-[20px] h-5 px-1.5 rounded-full flex items-center justify-center">
                              {messageUnreadCount > 99 ? '99+' : messageUnreadCount}
                            </span>
                          )}
                          <Lock size={14} className="text-amber-500" />
                        </Link>
                      </TooltipTrigger>
                      <TooltipContent side="right" className="max-w-xs">
                        <p>{hoverMessage}</p>
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    <Link
                      to={item.path}
                      onClick={(e) => handleMenuClick(e, item)}
                      className={`flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${
                        isActive ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <Icon size={20} />
                        <span className="text-sm font-medium">{item.label}</span>
                      </div>
                      {item.path === '/chat' && messageUnreadCount > 0 && (
                        <span className="bg-blue-600 text-white text-xs font-medium min-w-[20px] h-5 px-1.5 rounded-full flex items-center justify-center">
                          {messageUnreadCount > 99 ? '99+' : messageUnreadCount}
                        </span>
                      )}
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>
        </TooltipProvider>
      </nav>

      <div className="p-4 border-t border-gray-200">
        {pointsSystemEnabled && (
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 mb-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-blue-900">Reward Points</span>
              <Award size={18} className="text-blue-600" />
            </div>
            <p className="text-2xl font-bold text-blue-700">{displayPoints.toLocaleString()}</p>
            <p className="text-xs text-blue-600 mt-1">{displayTier} Member</p>
          </div>
        )}
        
        <button
          onClick={logout}
          className="w-full flex items-center justify-center space-x-2 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          data-testid="logout-btn"
        >
          <LogOut size={18} />
          <span>Sign Out</span>
        </button>
      </div>

      {/* Upgrade Prompt / Restriction Dialog */}
      <UpgradePrompt 
        isOpen={showUpgradePrompt}
        onClose={handleUpgradePromptClose}
        onReturnFocus={onRestoreFocus}
        feature={blockedFeature}
        restrictionMessage={blockedFeature && hasRestriction(featureToRestriction[blockedFeature])
          ? getRestrictionMessage(featureToRestriction[blockedFeature])
          : null}
      />

      <Dialog open={showUpgradeBlockedDialog} onOpenChange={handleUpgradeBlockedDialogChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Complete Company Registration</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm text-gray-700">
            <p>
              Complete your company registration and wait for approval before upgrading or making any payment.
            </p>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => handleUpgradeBlockedDialogChange(false)}>
                Close
              </Button>
              <Button
                className="bg-blue-600 hover:bg-blue-700 text-white"
                onClick={() => {
                  handleUpgradeBlockedDialogChange(false);
                  navigate(companyRegistration ? '/my-company-registration' : '/company-registration');
                }}
              >
                {companyRegistration ? 'View Registration' : 'Complete Registration'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      </aside>
    </>
  );
};

export default Sidebar;
